// src/components/proposal-formats/ProposalFormatsModePanel.tsx
//
// Proposals sub-panel inside `formsAndFormats`. Org-scoped editor for
// the page library, calculation rules, packages, and contact overrides.
//
// Layout: product-type rail → left authoring tabs (pages / calcs / packages)
// + right package PDF preview → post-sale todos.

import { useCallback, useEffect, useMemo, useState } from "react";
import { consumePendingProposalProductType } from "@/lib/workspaceModeNavigation";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useDualReadProductTypes } from "@/hooks/useDualReadProductTypes";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import { orgRowToProposalContact } from "@/lib/opportunity/proposalContactTypes";
import { listProposalAlternates } from "@/lib/opportunity/proposalAlternateClient";
import { findPrimaryProposalPackage } from "@/lib/opportunity/proposalPrimaryPackage";
import type { OpportunityFormatKey } from "@/types/opportunity";
import { PROPOSAL_TEXT_PRODUCT_TYPE_WILDCARD } from "@/lib/opportunity/proposalTextSections";
import { ProposalFormatCalculationsPanel } from "./ProposalFormatCalculationsPanel";
import { FormatDocumentSlotsSection } from "./FormatDocumentSlotsSection";
import { FormatProposalContactSection } from "./FormatProposalContactSection";
import { ProposalAlternatesSection } from "./ProposalAlternatesSection";
import { ProposalFormatPreviewPanel } from "./ProposalFormatPreviewPanel";
import { ProposalPagesPanel } from "./ProposalPagesPanel";
import { PostSaleTodosSection } from "./PostSaleTodosSection";
import { SectionTabSlider, type SectionTabOption } from "./SectionTabSlider";
import { DEFAULT_PROPOSAL_ALTERNATE_KEY } from "@/lib/opportunity/proposalAlternateTypes";
import type { PreviewSelection } from "@/lib/opportunity/proposalPreviewSelection";

const GENERIC_TAB = PROPOSAL_TEXT_PRODUCT_TYPE_WILDCARD;

type AuthoringTab = "pages" | "calculations" | "packages";

function formatKeyForProductType(productType: string): OpportunityFormatKey {
  if (productType === GENERIC_TAB) return "generic";
  return resolveManagerPresetFormatKey(productType);
}

export function ProposalFormatsModePanel() {
  const org = useOrgSettings();
  const productTypes = useDualReadProductTypes();
  const [productType, setProductType] = useState<string>(() => {
    return consumePendingProposalProductType() ?? GENERIC_TAB;
  });
  const [authoringTab, setAuthoringTab] = useState<AuthoringTab>("pages");
  const [documentSlotsTick, setDocumentSlotsTick] = useState(0);
  const [contactTick, setContactTick] = useState(0);
  const [previewTick, setPreviewTick] = useState(0);
  const [previewRenderRequestId, setPreviewRenderRequestId] = useState(0);
  const [previewSelection, setPreviewSelection] = useState<PreviewSelection>({
    kind: "package",
    alternateKey: DEFAULT_PROPOSAL_ALTERNATE_KEY,
  });
  const [packagesNeedAssembly, setPackagesNeedAssembly] = useState(false);

  const orgContactName = useMemo(
    () => orgRowToProposalContact(org.proposalContactRow, org.brandName, org.organizationName).orgName,
    [org.brandName, org.organizationName, org.proposalContactRow],
  );

  const sliderOptions = useMemo(() => {
    const fromCatalog = productTypes.map((p) => ({
      id: p.tag,
      label: p.label,
    }));
    return [...fromCatalog, { id: GENERIC_TAB, label: "Generic" }];
  }, [productTypes]);

  useEffect(() => {
    const pending = consumePendingProposalProductType();
    if (pending) setProductType(pending);
  }, []);

  useEffect(() => {
    if (sliderOptions.some((o) => o.id === productType)) return;
    setProductType(sliderOptions[0]?.id ?? GENERIC_TAB);
  }, [sliderOptions, productType]);

  const formatKey = formatKeyForProductType(productType);

  const refreshPackageAttention = useCallback(async () => {
    const r = await listProposalAlternates({
      format_key: formatKey,
      product_type: productType,
      archetype: "proposal",
    });
    if (!r.ok) {
      setPackagesNeedAssembly(false);
      return;
    }
    const proposalAlts = r.data.alternates.filter(
      (a) => (a.archetype ?? "proposal") === "proposal",
    );
    setPackagesNeedAssembly(!findPrimaryProposalPackage(proposalAlts));
  }, [formatKey, productType]);

  useEffect(() => {
    void refreshPackageAttention();
  }, [refreshPackageAttention]);

  const bumpPreview = useCallback(() => {
    setPreviewTick((t) => t + 1);
  }, []);

  const requestPreview = useCallback((selection: PreviewSelection) => {
    setPreviewSelection(selection);
    setPreviewTick((t) => t + 1);
    setPreviewRenderRequestId((t) => t + 1);
  }, []);

  const onPagesOrPackagesChanged = useCallback(() => {
    bumpPreview();
    void refreshPackageAttention();
  }, [bumpPreview, refreshPackageAttention]);

  const authoringTabs = useMemo((): ReadonlyArray<SectionTabOption<AuthoringTab>> => {
    return [
      { id: "pages", label: "Pages" },
      { id: "calculations", label: "Calculations" },
      {
        id: "packages",
        label: "Packages",
        tone: packagesNeedAssembly ? "warning" : "default",
        badge: packagesNeedAssembly ? "!" : undefined,
      },
    ];
  }, [packagesNeedAssembly]);

  return (
    <div className="space-y-5">
      <p className="mx-auto max-w-2xl text-center text-xs text-gray-500 dark:text-zinc-400">
        Pick a product type below. The <strong>Generic</strong> tab is the fallback template for any
        product without its own pages.
      </p>

      <div className="flex justify-center">
        <div
          {...{ [FIREFLY_TARGET_ATTR]: "ff-proposal-preset-list" }}
          className="inline-flex max-w-full flex-wrap justify-center gap-1 rounded-xl border border-gray-200 bg-white p-1 text-xs dark:border-zinc-700 dark:bg-zinc-900"
        >
          {sliderOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setProductType(opt.id)}
              className={
                "rounded-lg px-3 py-1.5 whitespace-nowrap transition-colors " +
                (productType === opt.id
                  ? "bg-indigo-600 text-white"
                  : "text-gray-700 hover:bg-gray-50 dark:text-zinc-200 dark:hover:bg-zinc-800")
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
        <div className="min-w-0 rounded-2xl border border-gray-200 bg-gray-50/40 p-3 dark:border-zinc-700 dark:bg-zinc-900/25">
          <div className="mb-3 flex justify-center">
            <SectionTabSlider
              options={authoringTabs}
              value={authoringTab}
              onChange={setAuthoringTab}
              ariaLabel="Proposal authoring"
            />
          </div>

          {authoringTab === "pages" ? (
            <div className="space-y-3">
              <ProposalPagesPanel
                formatKey={formatKey}
                productType={productType}
                onChanged={onPagesOrPackagesChanged}
                onPreviewPage={(pageId) => requestPreview({ kind: "page", pageId })}
              />
              <FormatDocumentSlotsSection
                formatKey={formatKey}
                productType={productType}
                reloadTick={documentSlotsTick}
                onChanged={() => setDocumentSlotsTick((t) => t + 1)}
              />
              <FormatProposalContactSection
                formatKey={formatKey}
                productType={productType}
                orgOrgName={orgContactName}
                reloadTick={contactTick}
                onSaved={() => {
                  setContactTick((t) => t + 1);
                  bumpPreview();
                }}
              />
            </div>
          ) : null}

          {authoringTab === "calculations" ? (
            <ProposalFormatCalculationsPanel
              formatKey={formatKey}
              productType={productType}
              productTypes={productTypes}
            />
          ) : null}

          {authoringTab === "packages" ? (
            <ProposalAlternatesSection
              formatKey={formatKey}
              productType={productType}
              onChanged={onPagesOrPackagesChanged}
              onPreviewPackage={(alternateKey) =>
                requestPreview({ kind: "package", alternateKey })
              }
            />
          ) : null}
        </div>

        <ProposalFormatPreviewPanel
          formatKey={formatKey}
          productType={productType}
          contentTick={previewTick}
          selection={previewSelection}
          onSelectionChange={setPreviewSelection}
          renderRequestId={previewRenderRequestId}
        />
      </div>

      <PostSaleTodosSection productType={productType} />
    </div>
  );
}
